/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import unionBy from 'lodash.unionby';
import uniqBy from 'lodash.uniqby';
import {resolveName} from '@ion/core/src/entities/EntityUtils';
import {info} from '@ion/core/src/logger/logger';
import {
  entityId,
  getExtendedAttributeValue
} from '@ion/core/src/services/ConnectUtils';
import {
  ApplicationFeature,
  GenericObject,
  Link,
  Permission,
  Role,
  Roles,
  User
} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {contains} from '@ion/core/src/utils/StringUtil';

/**
 * Defines the `Map` type alias for the `getUserPermissions` utility, which returns
 * a `Map` of string keys and `Permission` or `Link` values.
 */
export type PermissionMap = Map<string, Permission | Link>;

/**
 * Utility method which determines if a User is a Read-Only User based on the list
 * of `Roles` provided.
 *
 * Returns `true` if all permissions in the included `Roles` are read-only permissions,
 * otherwise, returns `false` if any permissions are non read-only permissions, such as
 * `create`, `update`, and / or `delete`.
 *
 * ```typescript
 *  import {isReadOnly} from '@ion/core/src/security/helpers';
 *
 *  // returns true if all permissions are read-only, otherwise, returns false if
 *  // any permissions are create, update, and / or delete permissions
 *  isReadOnly(roles);
 *
 * ```
 */
export const isReadOnly = (roles: Roles): boolean => {
  const EXCLUSION_PERMISSIONS = {
    create : true, update : true, delete : true
  };
  return !roles?.find(({permissions}) => (
    permissions?.find(({title}) => (
      EXCLUSION_PERMISSIONS[title?.split?.('-')?.at(-1)]
    ))
  ));
};

/**
 * The isRole provides a simple convenience which can be used to determine
 * if a given role is an iOn Application Role.
 *
 * ```typescript
 *
 *   import {isRole} from '@ion/core/src/security/helpers';
 *
 *   // determine if given role is an application role ...
 *   if (isRole(role, appKey)) {
 *     ...
 *   }
 *
 * ```
 *
 * @method  isRole
 * @param   {object} role The role which is to be determined to be
 *          an iOn Application role.
 * @param   {string} appKey The iOn Application (iOn) application key.
 * @returns {boolean} True if the provided Role is an iOn Application
 *          Role, otherwise, false.
 */
export const isRole = (role: Role, appKey: string) => {
  return contains(role.application.href, appKey);
};

/**
 * The getRoles function returns an array of iOn Application
 * specific Roles (if any) from the provided list of Roles. The list
 * of Roles must be an array of complete Role representations.
 *
 * ```typescript
 *
 *   import {getRoles} from 'security/helper';
 *
 *   // retrieve all application (iOn) roles ...
 *   const appRoles = getRoles(roles, appKey);
 *
 * ```
 *
 *
 * @method  getRoles
 * @param   {array} roles The list of CTC Role Entities to be inspected.
 * @returns {array} Returns an array of iOn Application specific Roles.
 */
export const getRoles = (roles: Role[], appKey: string): Role[] => {
  return roles.filter(role => isRole(role, appKey));
};

/**
 * The `getRole` function returns the role from the list of roles which
 * is associated with the given `appKey`.
 *
 * ```typescript
 *
 *   import {getRole} from '@ion/core/src/security/helpers';
 *
 *   // retrieve a reference to the role for the provided appKey ...
 *   const role = getRole(roles, appKey);
 *
 * ```
 *
 *
 * @method  getRole
 * @param   roles The list of CTC Role Entities to be inspected.
 * @param   appKey The application key for which the role in the list of
 *          roles is to be returned.
 * @returns The `Role` from the list of `Roles` which matches the given
 *          `appKey`.
 */
export const getRole = (roles: Role[], appKey: string): Role => {
  return roles?.find(role => isRole(role, appKey));
};

/**
 * The omitRoles function provides a utility which is the
 * inverse of the getRoles; that is, it returns all Roles
 * in the provided list which are not iOn Application Roles.
 *
 * ```typescript
 *
 *   import {omitRoles} from 'security/helper';
 *
 *   // retrieve all roles which are not application roles ...
 *   roles = omitRoles(roles, appKey);
 *
 * ```
 *
 *
 * @method  omitRoles
 * @param   {array} roles The list of CTC Role Entities to be inspected.
 * @returns {array} Returns an array of all roles from the list which
 *          are not iOn Application Roles.
 */
export const omitRoles = (roles: Role[], appKey: string) => {
  return roles.filter(role => !isRole(role, appKey));
};

/**
 * The getUserRoles function maps a User entity's roles array of HATEAOS Links
 * (href, title, rel) to their fully qualified Role entity representations for
 * the given iOn Application application. This allows for inspecting the Application
 * for which the Role is based, which would otherwise be unknown when inspecting
 * the HATEAOS Role Links.
 *
 * ```typescript
 *
 *   import {
 *     getRoles,
 *     getUserRoles
 *   } from 'security/helper';
 *
 *   // retrieve all application (iOn) roles ...
 *   const appRoles = getRoles(roles, appKey);
 *
 *   // retrieve all user application (iOn) roles ...
 *   const userRoles = getUserRoles(user, appRoles, appKey);
 *
 * ```
 *
 *
 * @method  getUserRoles
 * @param   {object} user The authenticated user entity retrieved
 *          from CTC services.
 * @param   {array} role The complete representation of all Roles
 *          for the given account retrieved from CTC services.
 * @param   {string} appKey The application key applicable to the
 *          Roles of interest (iOn Application / iOn appKey), as
 *          provided via environment.json.
 * @returns {array} Returns an array of fully qualified Role entities
 *          (their complete representations) for each matching role
 *          assigned to the User.
 */
export const getUserRoles = (user: User, roles: Role[], appKey: string) => {
  const userRoles = {};
  const appRoles  = [];
  user.roles.forEach(({href}) => {
    userRoles[href] = true;
  });
  roles.forEach(role => {
    if (contains(role.application.href, appKey) && userRoles[role._self.href]) {
      appRoles.push(role);
    }
  });
  return appRoles;
};

/**
 * Provides a utility which returns a map of all user permissions based on the
 * provided User Roles, whereby each key within the map is the permission name
 * (title), and the value is the permission.
 *
 * ```typescript
 *
 *   import {
 *     getRoles,
 *     getUserRoles,
 *     getUserPermissions
 *   } from '@ion/core/src/security/helper';
 *
 *   // retrieve all application (iOn) roles ...
 *   const appRoles = getRoles(roles, appKey);
 *
 *   // retrieve all user application roles ...
 *   const userRoles = getUserRoles(user, appRoles, appKey);
 *
 *   // create a map of all user role permissions ...
 *   const permissionsMap = getUserPermissions(userRoles);
 *
 * ```
 *
 * @param   `roles` An array of User assigned roles from which the permissions are
 *          to be returned.
 * @returns A map of all user role permissions whereby each key within the map is
 *          the permission name (title), and the value is the permission.
 */
export const getUserPermissions = (roles: (Role[] | Roles) | Link[]): PermissionMap => {
  const permissions = new Map<string, Permission | Link>();
  roles?.forEach((role: Role) => {
    role.permissions?.forEach((permission: Permission | Link) => {
      permissions.set(resolveName(permission), permission);
    });
  });
  return permissions;
};

/**
 * Provides a utility which, given a list of user Role entities, inspects
 * each Role for iOn Application Features, and returns a map where each key
 * represents the id of a Feature which the user has access.
 *
 * ```typescript
 *
 *   import {
 *     getRoles,
 *     getUserRoles,
 *     getAllowedFeatures
 *   } from 'security/helper';
 *
 *   // retrieve all application (iOn) roles ...
 *   const appRoles = getRoles(roles, appKey);
 *
 *   // retrieve all user application (iOn) roles ...
 *   const userRoles = getUserRoles(user, appRoles, appKey);
 *
 *   // retrieve all user role application features ...
 *   const features = getAllowedFeatures(userRoles);
 *
 * ```
 *
 *
 * @method  getAllowedFeatures
 * @param   {array}  roles The list of fully qualified role entities assigned
 *          to the authenticated User.
 * @param   {array} allFeatures All the application's features.
 * @returns {array} An array containing each Feature the User has access to
 *          based on their assigned iOn Application Roles, including default features.
 *
 */
export const getAllowedFeatures = (roles: Role[], allFeatures: ApplicationFeature[]) => {
  const allowedFeatures = [];
  const deniedFeatures = [];
  const setFeatures = (items = [], allowedFeatures) => {
    items.forEach(({id, name, features, allowed, description}: ApplicationFeature) => {
      if (allFeatures.find((item) => item.id === id)) {
        if (isFeatureAllowed(allowed)) {
          allowedFeatures.push({id, name, description});
        } else if (isFeatureDenied(allowed)) {
          deniedFeatures.push({id, name});
        }
        if (features?.length) {
          setFeatures(features, allowedFeatures);
        }
      }
    });
  };
  roles.forEach(role => {
    setFeatures(getExtendedAttributeValue(role, 'features'), allowedFeatures);
  });
  return applyDefaultFeatures(allowedFeatures, deniedFeatures, allFeatures);
};

/**
 * Provides a utility which, given a feature's allowed property,
 * determines whether that feature has to be allowed or not.
 *
 * In iOn Prime, allowed = true/false is stored alongside the role's
 * feature.  The allowed flag is not required to be serialized in Suite.
 * However for backward  compatibility with Prime, need to check if
 * allowed flag exists or is it true to determine whether the feature
 * is allowed or not.
 *
 * @param   allowed The allowed property of role's feature.
 * @returns Whether to allow the feature or not.
 */
const isFeatureAllowed = (allowed: boolean): boolean => {
  return (isUndefined(allowed) || allowed === true);
};

const isFeatureDenied = (allowed: boolean): boolean => {
  return (!isUndefined(allowed) && allowed === false);
};

/**
 * Given a list of Role permitted iOn Features, and all User assigned Roles,
 * filters the list based on Features which also require additional CTC
 * applications outside of iOn.
 *
 * @method verifyFeatures
 * @param  {array} allowed The list of allowed Role Features.
 * @param  {object} user The authenticated user entity.
 * @param  {array} validations A list of Features API feature constants against
 *         which to verify (e.g. ADD_ONS_CRASHBOXX, etc.).
 */
export const verifyFeatures = (features: ApplicationFeature[], user: User, validations: GenericObject) => {
  const verified     = [];
  const applications = {};
  user.applications.forEach(app => {
    const key = app.href.split('/applications/')[1];
    applications[key] = true;
  });
  features?.forEach(feature => {
    const application = validations[feature.id];
    if (!application || (applications[application])) {
      verified.push(feature);
    }
  });
  return verified;
};

/**
 * Provides a convenience method which returns all roles within the given
 * list which contain iOn Application Features.
 *
 * ```typescript
 *
 *   import {getFeatureRoles} from '@ion/core/src/security/helpers';
 *
 *   // retrieve all application feature roles ...
 *   const appRoles = getFeatureRoles(roles);
 *
 * ```
 *
 * @method  getFeatureRoles
 * @param   {array} roles The array of Roles which are to be inspected
 *          to determine if they contain iOn Application Features.
 * @returns {array} An array containing each Role in the given list which
 *          contains iOn Application Features.
 */
export const getFeatureRoles = (roles: Role[]) => {
  return roles.filter(role => {
    const {extendedAttribute} = role;
    if (extendedAttribute?.value) {
      const features = safeJSONParse(extendedAttribute.value as string) as any;
      return Boolean(features?.length);
    }
  });
};

/**
 * The compactFeatures function accepts an array of Features and compacts
 * them into an object where each key is the id of a Feature and the value
 * is set to true. This method is intended for fast lookups when determining
 * if a given User has access to a specific Feature.
 *
 * @method  compactFeatures
 * @param   {array} features An array containing representations of allowed
 *          Features, where each object in the array contains, at minimum,
 *          the id of a given feature.
 * @returns {object} An object containing keys for each Feature the User
 *          has been granted access to (via their roles). Each key is an
 *          id of the allowed Features, and each key's value is true.
 *          Features to which the User does not have access are omitted
 *          from the object entirely.
 */
export const compactFeatures = (features: ApplicationFeature[]): GenericObject => {
  const map = {};
  features?.forEach(({id, allowed}) => {
    if (isFeatureAllowed(allowed)) {
      map[id] = true;
    }
  });
  return map;
};

/**
 * The combinePermissions function provides a convenience utility which,
 * given two sets of permissions (updated, and existing), returns a new
 * array containing the updated permissions and the existing permissions
 * with all duplicates removed.
 *
 * ```typescript
 *   import {combinePermissions} from '@ion/core/src/security/helpers';
 *
 *   // consolidate updated and existing permissions for the given role ...
 *   const permissions = combinePermissions(feature.permissions, role.permissions);
 *
 * ```
 *
 * @method  combinePermissions
 * @param   {array} updated An array of new permissions to combine.
 * @param   {array} existing An array of existing permissions to combine.
 * @returns {array} An array containing the union of the two permissions
 *          arrays with all duplicates removed.
 */
export const combinePermissions = (
  updated: Permission[] | Link,
  existing: Permission[] | Link
): Permission[] => {
  return unionBy(updated, existing, 'href');
};

/**
 * The normalizePermissions function provides a convenience utility which
 * removes duplicate permissions from an array of permissions, returning
 * the normalized, reduced result.
 *
 * ```typescript
 *   import {normalizePermissions} from '@ion/core/src/security/helpers';
 *
 *   // remove all duplicate permissions ...
 *   const permissions = normalizePermissions(feature.permissions);
 *
 * ```
 *
 * @method  normalizePermissions
 * @param   {array} permissions An array of permissions containing possible
 *          duplicates.
 * @returns {array} An array of permissions based on the provided permissions
 *          array with all duplicates removed.
 */
export const normalizePermissions = (permissions: Link[]): Link[] => {
  return uniqBy(permissions, 'href');
};

/**
 * Given a set of Features, recursively iterates through each feature's
 * features list and combines all permissions for all features, returning
 * an array containing all combined permissions.
 *
 * ```typescript
 *
 *   import {getAllFeaturePermissions} from '@ion/core/src/security/helpers';
 *
 *   let permissions = getAllFeaturePermissions(features);
 *   console.log(permissions.length); // length of all feature permissions
 *
 * ```
 *
 *
 * @method  getAllFeaturePermissions
 * @param   {array} features The array of features for which all permissions
 *          are to be added to the all array.
 * @returns {array} An array of all feature permissions.
 */
export const getAllFeaturePermissions = (features: ApplicationFeature[]) => {
  const all = [];
  consolidatePermissions(features, all);
  return normalizePermissions(all);
};

/**
 * Given a set of Features, recursively iterates through each feature's
 * features list and picks out features containing default values.
 *
 * @method  consolidateDefaultFeatures
 * @param   {array} features The array of all application features.
 * @param   {array} all The flattened array of all application features having default values.
 * @returns {array} The updated all array containing list of all application features having default values.
 */
const consolidateDefaultFeatures = (features: ApplicationFeature[], all: ApplicationFeature[],
  parent: ApplicationFeature) => {
  features?.forEach((feature: any) => {
    const {id, name, features, default: defaultValue} = feature;
    if (!isUndefined(defaultValue)) {
      if (parent) {
        all.push({id: parent.id, name: parent.name, features: [{id, name, default: defaultValue}]});
      } else {
        all.push({id, name, default: defaultValue});
      }
    }
    if (features?.length) {
      consolidateDefaultFeatures(features, all, feature);
    }
  });
};

/**
 * Entry point function to recursively iterate through each feature's
 * features list and picks out features containing default values.
 *
 * @method  getDefaultFeatures
 * @param   {array} features The array of all application features.
 * @returns {array} The flattened array of all application features having default values.
 */
const getDefaultFeatures = (features: ApplicationFeature[]) => {
  const all = [];
  consolidateDefaultFeatures(features, all, null);
  return all;
};

/**
 * Given an array of features allowed by the role and a list of all application features,
 * get the list of default features, iterate through the list of default features and determine
 * whether or not a feature is allowed. If the feature was disallowed, then it stays that way.
 * If a feature didn't exist in the list (indicating not chosen previously), then it'll now be allowed by default.
 * This will be applied for newly added features which didn't exist previously, but have to be allowed by default.
 *
 * @method  applyDefaultFeatures
 * @param   {array} allowedfeatures The array of all features allowed by the role.
 * @param   {array} deniedfeatures The array of all features denied by the role.
 * @param   {array} allFeatures The array of all application features.
 * @returns {array} The updated allowedFeatures array with additional features allowed by default by the application.
 */
const applyDefaultFeatures = (allowedfeatures: ApplicationFeature[],
  deniedfeatures: ApplicationFeature[],
  allFeatures: ApplicationFeature[]): ApplicationFeature[] => {
  const defaultFeatures = getDefaultFeatures(allFeatures);
  defaultFeatures?.forEach((defaultFeature: any) => {
    let {id: parentId, features} = defaultFeature;
    let featureId = null;
    let defaultValue = null;
    let featureName = null;
    if (isUndefined(features) || features.length <= 0) {
      //This feature is at root level
      featureId = parentId;
      featureName = defaultFeature.name;
      defaultValue = defaultFeature.default;
      parentId = null;
    } else {
      featureId = features[0].id;
      featureName = features[0].name;
      defaultValue = features[0].default;
    }
    let isParentAllowed = false;
    if (parentId == null) {
      //Check and apply feature ids.
      isParentAllowed = true;
    } else {
      //First check if parent is allowed
      for (const f of allowedfeatures) {
        if (f.id === parentId) {
          isParentAllowed = true;
          break;
        }
      }
    }
    if (isParentAllowed) {
      let featureFound = false;
      const featureDenied = deniedfeatures.filter(({id}) => featureId === id);
      if (featureDenied?.length > 0) {
        featureFound = true;
      }
      for (const f of allowedfeatures) {
        if (f.id === featureId) {
          featureFound = true;
          break;
        }
      }
      if (!featureFound && defaultValue) {
        allowedfeatures.push({id: featureId, name: featureName});
      }
    }
  });
  return allowedfeatures;
};

/**
 * Recursively iterate through each feature's features list, check if the parent feature is allowed
 * and then apply the default value to the new feature.
 *
 * @method  findAndApplyFeature
 * @param   {array} rolefeatures The array of all features allowed by the role.
 * @param   {array} defaultFeature The application features that needs to be allowed by default.
 * @returns {array} The updated roleFeatures with the default value applied for the new feature.
 */
const findAndApplyFeature = (roleFeatures: ApplicationFeature[], defaultFeature: ApplicationFeature) => {
  const findAndApply = (roleFeatures: ApplicationFeature[], parentId: string, subFeature: ApplicationFeature) => {
    if (parentId === null) {
      const exists = roleFeatures.some(f => f.id === subFeature.id);
      if (!exists) {
        //Apply subFeature at root level and exit
        subFeature.allowed = subFeature.default;
        delete subFeature.default;
        roleFeatures.push(subFeature);
      }
      return;
    }
    for (const f of roleFeatures) {
      const {features, id, allowed} = f;
      if (id === parentId) {
        //Check if parent is allowed
        if (!isUndefined(allowed) && !allowed) {
          //Parent is not allowed. Then no need to apply default value to sub feature since parent itself isnt allowed.
          break;
        }
        //Prent found. Apply sub features
        let subFeatureFound = false;
        for (const subF of (features || [])) {
          if (subF.id === subFeature.id) {
            subFeatureFound = true;
            break;
          }
        }
        if (!subFeatureFound) {
          subFeature.allowed = subFeature.default;
          if (isUndefined(subFeature.features)) {
            subFeature.features = [];
          }
          delete subFeature.default;
          if (!f.features) {
            f.features = [];
          }
          f.features.push(subFeature);
          break;
        }
      } else {
        //Parent not found. Traverse parent's sub features.
        if (features?.length) {
          findAndApply(features, parentId, subFeature);
        }
      }
    }
  };

  const {id, features} = defaultFeature;
  return findAndApply(roleFeatures, features?.length ? id : null, features?.length ? features[0]: defaultFeature);
};

/**
 * The `consolidatePermissions` utility accepts a top-level list of features
 * and an array to which all permissions for all features will be added.
 *
 * ```typescript
 *
 *   import {consolidatePermissions} from '@ion/core/src/security/helpers';
 *
 *   let permissions = [];
 *   consolidatePermissions(features, permissions);
 *   console.log(permissions.length); // length of all feature permissions
 *
 * ```
 *
 *
 * @method consolidatePermissions
 * @param  {array} features The array of features for which all permissions
 *         are to be added to the all array.
 * @param  {array} all The array to which all feature permissions are to be added.
 */
export const consolidatePermissions = (features: any[], all: ApplicationFeature[]) => {
  features?.forEach((feature: any) => {
    const {features, permissions} = feature;
    all.splice(all.length, 0, ...permissions);
    if (features?.length) {
      consolidatePermissions(features, all);
    }
  });
};

/**
 * Provides a utility which returns a Role's Application Feature definitions (if any).
 * Feature definitions are defined as a collection of ApplicationFeature instances
 * within a Role's extendedAttributes.value property, stringified from an object
 * which contains a top-level "features" key.
 *
 * @param   {Role} role The CTC Application Role from which features are to be parsed.
 * @param   {ApplicationFeature} allFeatures The array of all Application features.
 * @returns {ApplicationFeature[]} The collection of application Features with defaults applied for the given
 *          Role (if any).
 */
export const getRoleFeatures = (role: Role,
  allFeatures: ApplicationFeature[]): ApplicationFeature[] => {
  let roleFeatures = [];
  if (role?.extendedAttribute?.value) {
    roleFeatures = getExtendedAttributeValue(role, 'features') as ApplicationFeature[];
    if (allFeatures) {
      const defaults = getDefaultFeatures(allFeatures);
      for (const d of defaults) {
        findAndApplyFeature(roleFeatures, d);
      }
    }
  }
  return roleFeatures;
};

/**
 * @createEnabledFeatures
 *
 * Given a list of Role Features, recursively iterates each feature and adds the id
 * of the Feature as a key to a map with a  value of true to indicate that the feature
 * exists and is available as a Role Feature within the current list Features.
 *
 * This function is used when editing a Role as it generates a map of the roles feature
 * ids which are subsequently used to determine which features in the full list of
 * application features should be enabled.
 *
 * @param    {ApplicationFeature[]} features The List of Role Features to inspect.
 * @returns  {GenericObject} A hash to contain the enabled features whereby, the id
 *           of each Feature listed is added to the hash as a key, with it's value
 *           being set to true, indicating the Feature exists within the provided
 *           list of Role Features.
 */
export const createEnabledFeatures = (
  features: ApplicationFeature[], parentId?: string, enabled: GenericObject = {}
): GenericObject => {
  features.forEach(({id, features, allowed}: ApplicationFeature) => {
    enabled[id] = allowed !== false;
    if (parentId && enabled[id]) {
      enabled[parentId] = true;
    }
    if (features?.length) {
      createEnabledFeatures(features, id, enabled);
    }
  });
  return enabled;
};

/**
 * Provides a utility which determines if the given User has access to the specified
 * application.
 *
 * @param   {User} user The to determine application access.
 * @param   {string} appKey The application key to be verified.
 * @returns {boolean} True if the user has access to the application, otherwise, false.
 */
export const hasApplication = (user: User, appKey: string): boolean => {
  return Boolean(user.roles?.find(({href}: Link) => entityId(href) === appKey));
};

/**
 * The toExtendedAttributes function provides a convenience utility which
 * accepts a features object and application and returns an object suitable
 * for use with CTC extendedAttributes; that is, an object containing the
 * stringified value and application link.
 *
 * ```typescript
 *   import {toExtendedAttributes} from '@ion/core/src/security/helpers';
 *
 *   // remove all duplicate permissions ...
 *   const extendedAttribute = toExtendedAttributes(features, application);
 *
 * ```
 *
 * @method  toExtendedAttributes
 * @param   {object|array} features A features object containing a features
 *          array, or the features array directly.
 * @returns {object} application A CTC Application link.
 */
export const toExtendedAttributes = (features: ApplicationFeature, application) => {
  return {
    value : JSON.stringify({
      features : (features.features ? features.features : features)
    }),
    application
  };
};

/**
 * The syncRoleEnabledStatus function takes an ApplicationFeature object and
 * recursively sets the enabled property of each feature (and sub-feature, if any)
 * to true. It returns a new object with the updated features.
 *
 * ```typescript
 *
 *  import {syncRoleEnabledStatus} from '@ion/core/src/security/helpers';
 *
 *  const updatedFeatures = syncRoleEnabledStatus(appFeatures);
 *  console.log(updatedFeatures);
 *
 * ```
 *
 * @param features An ApplicationFeature object that contains features to be updated.
 *                 Each feature can have nested sub-features under a features property.
 * @returns        A new ApplicationFeature object with the enabled property of all features
 *                 (and sub-features) set to true.
 */
export const syncRoleEnabledStatus = (features: {[key: string]: ApplicationFeature}) => {
  const updateStatus = (feature) => {
    if (feature.features) {
      feature.features = feature.features.map(updateStatus);
    }
    // Set the `enabled` property to true if it is not defined.
    feature.enabled = feature.enabled ?? feature.allowed ?? true;
    return feature;
  };
  const updated = {};
  for (const feature in features) {
    updated[feature] = updateStatus(features[feature]);
  }
  return updated;
};

/**
 * Provides a simple logger for outputting the Features permitted to the
 * User based on their assigned Role. This function should be called after
 * a call to the Features.initialize(...) has been made.
 *
 * @method logFeatures
 * @param  {array} permitted The array of Permitted Application Features
 *         which are to be logged.
 */
export const logFeatures = (permitted = [], log = false) => {
  info('Features API: Permitted Features');
  permitted.forEach(({name}) => {
    info(`\t${name}`);
  });
};

/**
 * Provides a simple logger for outputting the Routes permitted to the User
 * based on the User's Permitted Features. This function should be called
 * after a call to the  Features.getPermittedPages(...) has been made.
 *
 * @method logRoutes
 * @param  {array} permitted The array of Permitted Application Routes which
 *         are to be logged.
 */
export const logRoutes = (permitted = [], isInitial = true) => {
  isInitial && info('Features API: Permitted Routes');
  permitted.forEach(({route, featureRoutes}) => {
    info(`\t${route}`);
    if (featureRoutes?.length) {
      logRoutes(featureRoutes, false);
    }
  });
};

/**
 * A function which determines to validate rolename with  logged in user roles
 *
 * ```typescript
 *
 * import {hasRole} from '@ion/core/src/security/helpers';
 *
 * const isSuperAdmin = hasRole(roles, restrictedRoleIds);
 *
 * ```
 *
 * @param current user roles.
 * @param  role to be verified.
 * @returns true if restrictedRoleIds exist in user roles otherwise, false.
 */
export const hasRole = (roles: Link[], restrictedRoleIds: number[]): boolean => {
  return Boolean(roles?.find(({href}: Link) => restrictedRoleIds?.indexOf(entityId(href)) >= 0));
};

/**
 * Helper which notifies developers to implement the given abstract method.
 *
 * @param operation the name of the abstract method that needs implementing.
 */
export const throwAbstractException = (operation: string) => {
  throw new Error(`${operation} must be implemented by sub-classes.`);
};
